import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Overlap size mappings
 */
const OVERLAP_MAP = {
  none: "gap-2",
  sm: "-space-x-1",
  md: "-space-x-2",
  lg: "-space-x-3",
} as const;

export interface IconStackProps {
  /**
   * Icon elements to stack
   */
  children: React.ReactNode;

  /**
   * Maximum number of icons to display before showing "+N"
   * @default undefined (show all)
   */
  max?: number;

  /**
   * Amount of overlap between icons
   * @default "md"
   */
  overlap?: keyof typeof OVERLAP_MAP;

  /**
   * Stack direction
   * @default "horizontal"
   */
  direction?: "horizontal" | "vertical";

  /**
   * Show count badge for overflow icons
   * @default true
   */
  showCount?: boolean;

  /**
   * Click handler for the overflow count badge
   */
  onOverflowClick?: () => void;

  /**
   * Additional className for the container
   */
  className?: string;

  /**
   * Size of the icons (used for overflow badge sizing)
   * @default "md"
   */
  size?: "sm" | "md" | "lg" | "xl";
}

/**
 * Stack multiple icons with optional overlap and overflow handling
 *
 * @example
 * ```tsx
 * // Basic stack with overlap
 * <IconStack overlap="md">
 *   <DiscordIcon size="md" />
 *   <TelegramIcon size="md" />
 *   <XIcon size="md" />
 * </IconStack>
 *
 * // Limited display with overflow handling
 * // Shows first 3 icons, remaining count as badge
 * <IconStack max={3} overlap="md" onOverflowClick={() => showAll()}>
 *   <DiscordIcon />
 *   <TelegramIcon />
 *   <XIcon />
 *   <LinkedInIcon />
 *   <GithubIcon />
 * </IconStack>
 *
 * // Vertical stack
 * <IconStack direction="vertical" overlap="sm">
 *   <Icon1 />
 *   <Icon2 />
 *   <Icon3 />
 * </IconStack>
 * ```
 */
export const IconStack = React.forwardRef<HTMLDivElement, IconStackProps>(
  (
    {
      children,
      max,
      overlap = "md",
      direction = "horizontal",
      showCount = true,
      onOverflowClick,
      className,
      size = "md",
    },
    ref,
  ) => {
    const childArray = React.Children.toArray(children);
    const hasOverflow = max !== undefined && childArray.length > max;
    const visibleChildren = hasOverflow ? childArray.slice(0, max) : childArray;
    const overflowCount = hasOverflow ? childArray.length - max : 0;

    // Size mappings for overflow badge
    const badgeSizeMap = {
      sm: "h-4 w-4 text-[10px]",
      md: "h-6 w-6 text-xs",
      lg: "h-8 w-8 text-sm",
      xl: "h-12 w-12 text-base",
    };

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center",
          direction === "horizontal" ? OVERLAP_MAP[overlap] : "flex-col -space-y-2",
          className,
        )}
      >
        {visibleChildren.map((child, index) => (
          <div key={index} className="inline-flex items-center justify-center rounded-full p-1.5 bg-cede-bg-component">
            {child}
          </div>
        ))}

        {hasOverflow && showCount && (
          <div
            className={cn(
              "inline-flex items-center justify-center rounded-full",
              "bg-cede-bg-component border-2 border-cede-bg-primary",
              "font-medium text-cede-text-primary",
              badgeSizeMap[size],
              onOverflowClick && "cursor-pointer hover:bg-cede-bg-secondary transition-colors",
            )}
            onClick={onOverflowClick}
            role={onOverflowClick ? "button" : undefined}
            tabIndex={onOverflowClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (onOverflowClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onOverflowClick();
              }
            }}
            aria-label={`${overflowCount} more icons`}
          >
            +{overflowCount}
          </div>
        )}
      </div>
    );
  },
);

IconStack.displayName = "IconStack";
